"use client";

import { type ReactNode, useCallback } from "react";

import Image from "next/image";

import { ImageBehaviourContext, type ImageWrapperProps } from "@jti/core-components/components/ImageBehaviour";

import { convertDimensionToInt } from "@/utils/convertDimensionToInt";
import { isOptimizedMagentoImageUrl } from "@/utils/isOptimizedMagentoImageUrl";
import { removeUrlQueryParams } from "@/utils/removeUrlQueryParams";

import type { ImageDimensionsMap } from "../utils/getImageDimensionsFromUrls";

type ImageBehaviourContextProviderProps = {
  children: ReactNode;
  imageDimensionsDict?: ImageDimensionsMap;
  isNextImageDisabled: boolean;
};

const emptyImageDimensionsDict: ImageDimensionsMap = {};

export const ImageBehaviourContextProvider = ({
  children,
  imageDimensionsDict,
  isNextImageDisabled,
}: ImageBehaviourContextProviderProps) => {
  const stableImageDimensionsDict = imageDimensionsDict ?? emptyImageDimensionsDict;

  const renderImage = useCallback(
    ({ alt, src, width, height, ...props }: ImageWrapperProps) => {
      if (typeof src !== "string") {
        console.error("ImageBehaviour: src is required! Falling back to <img>");
        return (
          // Passing original props for native img behavior
          // eslint-disable-next-line @next/next/no-img-element
          <img alt={alt} src={src} width={width} height={height} {...props} />
        );
      }

      // Check if Next/Image is disabled, or the asset has already been
      // optimized at the Magento origin (Cloudflare Image Resizing). In both
      // cases we render a native <img> so the asset skips /_next/image and is
      // not optimized a second time.
      if (isNextImageDisabled || isOptimizedMagentoImageUrl(src)) {
        return (
          // Passing original props for native img behavior
          // eslint-disable-next-line @next/next/no-img-element
          <img alt={alt} src={src} width={width} height={height} {...props} />
        );
      }

      // Convert width and height to integer values
      const convertedWidth = convertDimensionToInt(width);
      const convertedHeight = convertDimensionToInt(height);

      // Remove query params for consistent lookup
      const lookupSrc = removeUrlQueryParams(src);

      // Lookup dimensions from dict if props are missing
      const lookupDimensions = lookupSrc ? stableImageDimensionsDict[lookupSrc] : null;
      const finalWidth = convertedWidth || lookupDimensions?.width;
      const finalHeight = convertedHeight || lookupDimensions?.height;

      // Determine if we should use fill layout (missing width or height)
      const isFillLayout = !finalWidth || !finalHeight;

      return (
        <Image
          alt={alt ?? ""}
          src={src}
          width={isFillLayout ? undefined : finalWidth}
          height={isFillLayout ? undefined : finalHeight}
          fill={isFillLayout}
          {...props}
        />
      );
    },
    [isNextImageDisabled, stableImageDimensionsDict]
  );

  return <ImageBehaviourContext.Provider value={renderImage}>{children}</ImageBehaviourContext.Provider>;
};
